/**
 * Classification of spin-transport failures so the UI can show
 * accurate error messaging instead of a generic "connection lost"
 * for every kind of failure.
 *
 * - "network"  — no answer from the backend: fetch rejects, the `AbortSignal.timeout` deadline, DNS/TLS errors, and
 *                a 5xx whose body is not the backend's own (a proxy's or the edge's error page, decision 80): the
 *                request or its reply was lost on the way.
 * - "server"   — the backend's own 5xx (outage, overload): its error body carries `responseMetadata` or a
 *                `correlationId`.
 * - "protocol" — backend response is non-conformant: malformed JSON, body
 *                missing required fields, unknown action/event type, or
 *                domain-validator failure (free-spin counts violate
 *                invariants, etc.).
 * - "session"  — 401: the session is over; `statusMessage` says why when the backend named it
 *                (`session_superseded`: the game was opened again elsewhere, decision 80).
 *
 * The shell's recovery for every kind is a reload, never a retry (decision 17);
 * the kind decides the words. Until 2026-09-26 the notes above still named a
 * per-kind "wait + retry" nothing does (review finding 83).
 */
export type SpinTransportErrorKind = "network" | "server" | "protocol" | "session";

export class SpinTransportError extends Error {
  public readonly kind: SpinTransportErrorKind;
  /** The backend's code (`responseMetadata.statusMessage`) when its error reply named one. */
  public readonly statusMessage?: string;

  public constructor(kind: SpinTransportErrorKind, message: string, statusMessage?: string) {
    super(message);
    this.name = "SpinTransportError";
    this.kind = kind;
    this.statusMessage = statusMessage;
  }
}
